import React, { FormEvent, useCallback, useState } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

interface FormErrors {
  clinicName?: string;
}

function createErrorMessage(error: unknown): FormErrors & { form?: string } {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "Clinic could not be created. Try again." };
  }

  switch (error.code) {
    case "DUPLICATE_CLINIC":
      return { clinicName: "A clinic with this name already exists." };
    case "INVALID_CLINIC_NAME":
      return { clinicName: "Enter a valid clinic name." };
    case "THERAPIST_NOT_FOUND":
      return {
        form: "One or more selected therapists are no longer available. Refresh and try again.",
      };
    case "NOT_AUTHORIZED":
    case "NOT_FOUND":
      return { form: "This portal record is unavailable." };
    default:
      return { form: "Clinic could not be created. Try again." };
  }
}

export function AddClinicPage({ clinicianCode }: { clinicianCode: string }) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getTherapists(clinicianCode, signal),
    [clinicianCode],
  );
  const therapistState = usePortalData(request);
  const mutation = usePortalMutation();
  const [clinicName, setClinicName] = useState("");
  const [selectedTherapistIds, setSelectedTherapistIds] = useState<string[]>(
    [],
  );
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const data =
    therapistState.status === "success" ||
    therapistState.status === "refreshing"
      ? therapistState.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null,
  );
  const isSubmitting = mutation.state.status === "pending";
  const apiErrors =
    mutation.state.status === "error"
      ? createErrorMessage(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const toggleTherapist = (therapistId: string) => {
    clearErrors();
    setSelectedTherapistIds((current) =>
      current.includes(therapistId)
        ? current.filter((id) => id !== therapistId)
        : [...current, therapistId],
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedName = clinicName.trim();
    const nextErrors: FormErrors = {};
    if (!normalizedName) nextErrors.clinicName = "Clinic name is required.";
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    const result = await mutation.run("create-clinic", (signal) =>
      clinicianPortalApi.createClinic(
        clinicianCode,
        normalizedName,
        selectedTherapistIds,
        signal,
      ),
    );
    if (result?.created) {
      navigateWithinPortal(
        withClinicianCode("/clinician-portal/admin/clinics", clinicianCode),
      );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clinics"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Add Clinic"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              {
                label: "Clinics",
                path: "/clinician-portal/admin/clinics",
              },
              { label: "Add Clinic" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {therapistState.status === "loading" ? (
          <LoadingState label="Loading clinic form" />
        ) : null}
        {therapistState.status === "error" ? (
          <ErrorState
            error={therapistState.error}
            onRetry={therapistState.refresh}
          />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Clinic creation is unavailable")} />
        ) : null}
        {available && data ? (
          <section
            className="cp-record-form"
            aria-label="Add Clinic form"
          >
            <div className="cp-record-form__header">
              <p>Create a clinic and optionally assign clinicians.</p>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="cp-form-field">
                <label htmlFor="clinic-name">
                  Clinic Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="clinic-name"
                  className="cp-form-input"
                  type="text"
                  value={clinicName}
                  placeholder="Enter clinic name"
                  required
                  aria-invalid={Boolean(errors.clinicName)}
                  aria-describedby={
                    errors.clinicName ? "clinic-name-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setClinicName(event.target.value);
                  }}
                />
                {errors.clinicName ? (
                  <p className="cp-form-error" id="clinic-name-error">
                    {errors.clinicName}
                  </p>
                ) : null}
              </div>

              <fieldset className="cp-form-field cp-form-fieldset">
                <legend>Therapists</legend>
                {data.therapists.length ? (
                  <div className="cp-checkbox-list">
                    {data.therapists.map((therapist) => (
                      <label className="cp-checkbox-option" key={therapist.id}>
                        <input
                          type="checkbox"
                          value={therapist.id}
                          checked={selectedTherapistIds.includes(therapist.id)}
                          onChange={() => toggleTherapist(therapist.id)}
                        />
                        <span>
                          <strong>{therapist.therapistName || "—"}</strong>
                          <small>{therapist.email || "—"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-form-empty">
                    No therapist accounts are currently available. You can still
                    create this clinic without a therapist assignment.
                  </p>
                )}
              </fieldset>

              {apiErrors.form ? (
                <p className="cp-form-error cp-form-error--panel" role="alert">
                  {apiErrors.form}
                </p>
              ) : null}

              <div className="cp-form-actions cp-form-actions--sticky">
                <PortalLink
                  className="cp-button cp-button--secondary"
                  path="/clinician-portal/admin/clinics"
                  clinicianCode={clinicianCode}
                >
                  Cancel
                </PortalLink>
                <button
                  type="submit"
                  className="cp-button cp-button--primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating…" : "Create Clinic"}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
